import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, ShieldCheck } from 'lucide-react';

/**
 * Full-screen branded overlay shown the instant Place Order is clicked.
 * Stays mounted until the next page (ReviewOrderPage) takes over — and
 * is also reused as ReviewOrderPage's initial-fetch loading state so
 * the customer sees one continuous animated experience between the
 * /place call and the rendered Review page.
 *
 * Hero animation: a custom-rendered EG3 knight loop video served from
 * /loading-spinner.mp4 (cropped + compressed from the artist's source
 * to 360×480, ~680 KB, no audio, infinite loop). Replaces the previous
 * CSS-only spinner with a full-motion mascot the customer remembers.
 *
 * Reassuring rotating copy cycles every 4 s so each line has time to
 * be read but the customer never feels "stuck on one frozen string."
 *
 * Also blocks Esc/refresh-style interactions so a refresh during /place
 * can't spawn a duplicate draft order in the wild.
 */

// Rotating "we're working on it" copy. Customer-vetted: every line
// reassures, shows progress, and subtly discourages resubmits without
// sounding robotic or desperate.
const DEFAULT_STATUS_MESSAGES = [
  'Just a moment — putting everything together for you.',
  "We're making it happen… sit tight.",
  'Almost ready — this will be worth the wait.',
  "Hang in there — we're just wrapping things up.",
  'Getting things just right for you…',
];

// Cadence chosen empirically: 4 s gives each line ~enough time to be
// fully read (avg adult reading speed ≈ 4 words/s) but cycles fast
// enough that even a 5-second wait sees a fresh second line. Slow
// enough that it never feels twitchy or robotic.
const MESSAGE_INTERVAL_MS = 4000;

export const PlaceOrderLoadingOverlay = ({ open, messages, headline }) => {
  const STATUS_MESSAGES = (messages && messages.length > 0)
    ? messages
    : DEFAULT_STATUS_MESSAGES;
  const [msgIdx, setMsgIdx] = useState(0);
  const startedAt = useRef(0);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setMsgIdx(0);
      startedAt.current = 0;
      return undefined;
    }
    startedAt.current = Date.now();
    setMsgIdx(0);
    const id = window.setInterval(() => {
      setMsgIdx((n) => (n + 1) % STATUS_MESSAGES.length);
    }, MESSAGE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [open, STATUS_MESSAGES.length]);

  // Some browsers (notably iOS Safari) block autoplay until the video
  // is in the DOM AND ready. Give it a deterministic kick on mount so
  // the customer never sees a stalled first frame.
  useEffect(() => {
    if (!open) return;
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => v.play?.().catch(() => {});
    tryPlay();
    v.addEventListener('canplay', tryPlay, { once: true });
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="place-order-overlay"
          data-testid="place-order-loading-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          // Block all interaction with the page underneath. Customer
          // can't accidentally re-click Place Order or hit Back.
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#14281D]/85 backdrop-blur-sm"
          role="dialog"
          aria-live="polite"
          aria-label="Placing your order"
          onKeyDown={(e) => {
            // Prevent Esc from dismissing — the overlay must persist
            // until navigation actually swaps the page.
            if (e.key === 'Escape') e.preventDefault();
          }}
        >
          <motion.div
            initial={{ y: 12, scale: 0.96 }}
            animate={{ y: 0, scale: 1 }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
            // Wider popup (560 px) gives the knight loop room to
            // breathe without crowding the cycling status copy. White
            // surface intentionally matches the video's white
            // background so the loop reads as part of the popup
            // surface, not a video element pasted on top.
            className="relative w-[min(560px,94vw)] rounded-2xl bg-white px-8 py-8 shadow-2xl"
          >
            <div className="flex flex-col items-center text-center">
              {/* Hero — looping knight video, ~640 KB, no audio, 30fps.
                  Sized to ~280px wide so the entire figure (head to
                  feet) is visible. NO `rounded-lg` or `bg-white` on
                  the video itself — the source already has a clean
                  white background, and explicit framing would create
                  the "card-within-card" look the operator flagged.

                  IMPORTANT: the pulsing emerald glow is animated on a
                  WRAPPER div, NOT on the <video> element itself. Per
                  Chromium's compositing rules, a `filter` animation on
                  a video element forces the rendering pipeline to
                  re-rasterise the video bitmap on every animation
                  frame, which produces visible judder. Wrapping the
                  video in a div with the filter animation lets the
                  browser keep the video on its own GPU layer and
                  animate the wrapper independently — playback stays
                  smooth. */}
              <motion.div
                animate={{
                  filter: [
                    'drop-shadow(0 0 10px rgba(111, 191, 115, 0.18))',
                    'drop-shadow(0 0 22px rgba(111, 191, 115, 0.45))',
                    'drop-shadow(0 0 10px rgba(111, 191, 115, 0.18))',
                  ],
                }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className="mb-2 relative"
                style={{ willChange: 'filter' }}
              >
                <video
                  ref={videoRef}
                  src="/loading-spinner.mp4"
                  poster="/loading-spinner-poster.jpg"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  disablePictureInPicture
                  controlsList="nodownload nofullscreen noremoteplayback"
                  className="w-72 h-auto pointer-events-none select-none block"
                  data-testid="place-order-loading-video"
                />
                {/* Grounding shadow (iter104b) — soft grey ellipse beneath
                    the knight's rocky base so the figure doesn't read as
                    floating in the panel. Pure CSS — `radial-gradient`
                    on a 0-height div sized to the figure's foot
                    footprint. Subtle pulse synced loosely with the
                    breath effect. `mix-blend-mode: multiply` so the
                    shadow darkens the white card surface naturally
                    without flat-overlaying it. */}
                <motion.div
                  aria-hidden="true"
                  className="absolute left-1/2 -bottom-1 -translate-x-1/2 pointer-events-none"
                  style={{
                    width: '180px',
                    height: '14px',
                    background:
                      'radial-gradient(ellipse 50% 100% at 50% 50%, rgba(15,43,24,0.30) 0%, rgba(15,43,24,0.18) 35%, rgba(15,43,24,0) 75%)',
                    filter: 'blur(2px)',
                    mixBlendMode: 'multiply',
                    willChange: 'transform, opacity',
                  }}
                  animate={{ scaleX: [1, 0.94, 1], opacity: [0.85, 1, 0.85] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                />
              </motion.div>

              {/* Cycling status copy */}
              <div className="h-12 mb-3 overflow-hidden flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={msgIdx}
                    initial={{ y: 18, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -18, opacity: 0 }}
                    transition={{ duration: 0.32, ease: 'easeOut' }}
                    className="text-base font-semibold text-[#14281D] leading-tight px-2"
                    data-testid="place-order-status-text"
                  >
                    {STATUS_MESSAGES[msgIdx]}
                  </motion.p>
                </AnimatePresence>
              </div>

              {/* Slow-moving gradient progress bar — uses
                  background-position-x animation on a wide multi-stop
                  gradient instead of x-translating a child div. The
                  result is a soft, continuously shifting wash of green
                  that reads as "working in the background" rather
                  than the previous fast-traveling stripe (which felt
                  out of place against the slow video and made the
                  customer's eye dart). 6 s per cycle is calibrated to
                  feel relaxed yet alive — slow enough not to compete
                  for attention with the cycling status copy and the
                  knight loop, fast enough to obviously be moving. */}
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-4">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    backgroundImage:
                      'linear-gradient(90deg, rgba(111,191,115,0.0) 0%, rgba(111,191,115,0.55) 25%, #4A7C59 50%, rgba(111,191,115,0.55) 75%, rgba(111,191,115,0.0) 100%)',
                    backgroundSize: '300% 100%',
                    width: '100%',
                    willChange: 'background-position',
                  }}
                  animate={{ backgroundPositionX: ['0%', '100%'] }}
                  transition={{ repeat: Infinity, duration: 6, ease: 'linear' }}
                />
              </div>

              <p className="text-sm font-bold text-[#14281D] leading-snug">
                {headline || "Hold on, we're making sure everything is perfect."}
              </p>

              {/* "Do not refresh" warning — visually prominent because a
                  refresh during /place or /submit can produce a duplicate
                  draft / charge in the wild. */}
              <div className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-left">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 leading-snug">
                  <p className="font-semibold mb-0.5">
                    Please do not refresh this page.
                  </p>
                  <p>Do not close your browser or click &lsquo;back&rsquo;.</p>
                </div>
              </div>

              {/* Trust line */}
              <p className="mt-3 text-[11px] text-gray-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                Secured by Evergreen Graphics
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlaceOrderLoadingOverlay;
